import React from 'react';
import styled from 'styled-components';
import { ApiHeader } from '../../components/ApiHeader/ApiHeader';
import { InfoLabel } from '../../components/InfoLabel';
import { PageSection } from '../../components/PageSection';
import Spacer from '../../components/Spacer';
import { createFragmentContainer, graphql } from 'react-relay';
import SubscriptionUsage from '../../components/SubscriptionUsage';
import { HiddenContent } from '../../components/HiddenContent';
import { Subscription_subscription } from './__generated__/Subscription_subscription.graphql';
import CostCalculator from '../../components/CostCalculator';
import PricingCard from '../../components/PricingCard';
import Breadcrumb from '../../components/Breadcrumb';
import Decimal from 'decimal.js';
import Button from '../../components/input/Button';
import { HelpText } from '../../components/HelpText';
import { makeMutation } from '../../api/helpers';
import { DeleteSubscription } from '../../api/mutations';
import { useToastDispatch } from '../../components/Toast/ToastProvider';
import { useRouter } from 'found';
import { routes } from '../../routes';

const Container = styled.div`
  flex: 1;
`;

const UsagePlaceholder = styled.div`
  min-height: 460px;
`;

type Props = {
  subscription: Subscription_subscription;
  subscriptionFee: number;
};

function Subscription({ subscription, subscriptionFee }: Props) {
  document.title = `Subscriptions - ${subscription.service.name} - PayPI`;

  const dispatch = useToastDispatch();
  const { router } = useRouter();

  const deleteSubscription = async () => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      await makeMutation(DeleteSubscription, {
        subscriptionID: subscription.id,
      });
      dispatch({ text: 'Subscription deleted successfully', alert: 'success' });
      router.push(routes.subscriptions);
    } catch (err) {
      dispatch({ text: 'Unable to delete subscription', alert: 'error' });
    }
  };

  return (
    <Container>
      <Breadcrumb
        crumbs={[
          {
            text: 'Subscriptions',
            link: '/subscriptions',
          },
          {
            text: subscription.service.name,
          },
        ]}
      />
      <ApiHeader
        imageURL={subscription?.service?.imageURL || ''}
        title={subscription?.service?.name || ''}
        description={subscription?.service?.description}
      />
      <Spacer vertical spacing={5} />
      <PageSection
        title='Authentication'
        subtitle='Authentication information is used to make requests to the API, see the API documentation for details on how to use this.'
      >
        <Spacer vertical spacing={2} />
        <InfoLabel
          label='API SECRET'
          tooltip={{
            title: 'API Secret',
            category: 'Subscriptions',
            description:
              'The secret key used to authenticate you to the API servers',
            link: 'https://docs.paypi.dev/subscriptions#api-secret',
          }}
        >
          <HiddenContent
            secret={subscription?.secret}
            blurredText={
              subscription?.secret || 'Some random text that is blurred'
            }
          />
        </InfoLabel>
      </PageSection>
      <Spacer vertical spacing={5} />
      <PageSection title='My Usage'>
        <React.Suspense fallback={<UsagePlaceholder />}>
          <SubscriptionUsage />
        </React.Suspense>
      </PageSection>
      <Spacer vertical spacing={5} />
      <PageSection
        title='Pricing'
        subtitle='The cost of any requests made will be added to your subscription each month.'
      ></PageSection>
      {subscription?.service?.charges?.edges?.map((charge) => (
        <>
          <PricingCard
            chargeType={charge?.node?.chargeType ?? 'Unknown charge type'}
            title={charge?.node?.name ?? ''}
            description={charge?.node?.description ?? ''}
            chargePrice={new Decimal(charge?.node?.price.value ?? 0)
              .add(new Decimal(charge?.node?.salesTax.value ?? 0))
              .toNumber()}
            currency={charge?.node?.price.currency ?? ''}
          />
          <Spacer vertical spacing={1} />
        </>
      ))}
      <Spacer vertical spacing={2} />
      {subscription?.service && (
        <CostCalculator
          service={subscription?.service}
          subscriptionFee={subscriptionFee}
        />
      )}
      <Spacer vertical spacing={5} />
      <PageSection title='Subscription Settings' subtitle='' />
      <HelpText type='help'>
        If you don't use your subscription in any month, you aren't charged
        anything.
      </HelpText>
      <Spacer vertical spacing={2} />
      <Button
        background={'#F85C68'}
        color={'white'}
        width={'300px'}
        onClick={deleteSubscription}
      >
        Delete Subscription
      </Button>
      <Spacer vertical spacing={5} />
    </Container>
  );
}

export default createFragmentContainer(Subscription, {
  subscription: graphql`
    fragment Subscription_subscription on Subscription {
      id
      service {
        id
        name
        description
        imageURL
        charges(first: 100) {
          edges {
            node {
              id
              chargeType
              name
              description
              price {
                value
                currency
              }
              metricName
              salesTax {
                value
                currency
              }
            }
          }
        }
        ...CostCalculator_service
      }
      secret
    }
  `,
});
